import axios, { AxiosError, AxiosResponse } from 'axios';
import { observable, makeObservable, action, computed, runInAction } from 'mobx';
import { IBrickSelection, IPrice } from './types';

import { MainStore } from './MainStore';
import defaultStyles from '../styles/default.scss';
import { AvailabilityState } from '../components/orderModule/deliveryTime/deliveryTime';
import { decodeHTML } from '../helpers/html-helper';

function getWithVatValue(priceModeCookie?: 'Gross' | 'Net') {
    let result;

    switch (priceModeCookie) {
        case 'Gross':
            result = true;
            break;
        case 'Net':
            result = false;
            break;
        default:
            result =
                typeof document !== 'undefined'
                    ? (document.getElementsByClassName('current-pricemode-gross') || [{}]).length > 0
                    : true;
    }

    return result;
}

class OrderModuleStore {
    mainStore: MainStore;
    itemOrigin: string;
    mpcItemOrigin = 'DetailsPage';
    withVat: boolean;
    variantFlexSetId: string | null = '';
    compareShareBookmarkMobileExpanded: boolean;
    selectedBrickNo: number | null = null;
    selectedBrickPosition: number;
    articleCount: number;
    stickHeaderHeight = 0;
    readonly articleCountMin: number = 1;
    readonly articleCountMax: number = 9999;

    constructor(store: MainStore, initialPriceMode?: 'Gross' | 'Net') {
        this.mainStore = store;
        this.withVat = getWithVatValue(initialPriceMode);
        // 
        this.articleCount = store.fetchedData.orderModule.flexibleSetCount 
            && store.fetchedData.orderModule.flexibleSetCount > 0 ? store.fetchedData.orderModule.flexibleSetCount : 1;
        this.compareShareBookmarkMobileExpanded = false;
        this.selectedBrickPosition = 0;

        makeObservable(this, {
            withVat: observable,
            articleCount: observable,
            stickHeaderHeight: observable,
            compareShareBookmarkMobileExpanded: observable,
            selectedBrickNo: observable,
            selectedBrickPosition: observable,
            variantFlexSetId: observable,
            toggleWithVat: action.bound,
            setPriceModeCookie: action,
            setArticleCount: action.bound,
            setStickHeaderHeight: action.bound,
            saveVariantFlexSetId: action.bound,
            toggleCompareShareBookmarkMobileExpanded: action.bound,
            setSelectedBrickNo: action.bound,
            setSelectedBrickPosition: action.bound,
            basePriceString: computed,
            articleUnit: computed,
            titleAddon: computed,
            deliveryTimeString: computed,
            deliveryTimeStatusColor: computed,
            isSale: computed,
            shouldRenderBasePrice: computed,
            soldOutArticleStatusColor: computed
        });

        // normal methods that don't change state
        this.getPriceString = this.getPriceString.bind(this);
        this.updateBasketItemCount = this.updateBasketItemCount.bind(this);
        this.onPriceModeChangedEvent = this.onPriceModeChangedEvent.bind(this);

        if (typeof window !== 'undefined') {
            window.shell.subscribeTo('PriceModeChanged', newPriceMode => {
                this.onPriceModeChangedEvent(newPriceMode);
            });
        }

        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.forEach((value, key) => {
                if (key.localeCompare('itemOrigin', undefined, { sensitivity: 'accent' }) === 0)
                    this.itemOrigin = value;
            });
            if (this.itemOrigin) this.mpcItemOrigin = this.itemOrigin;
        }
    }

    getPriceString(price?: IPrice): string {
        if (price) {
            let localizedPriceString: string;
            let priceString: string;
            if (this.mainStore.isGlobal) {
                priceString = price.netValue.toFixed(2);
            } else {
                priceString = (this.withVat ? price.grossValue : price.netValue).toFixed(2);
            }

            const priceStringSplit = priceString.split('.');

            let intString = priceStringSplit[0];
            let seperatedIntString = '';
            const numberGroupSeparator = this.mainStore.dataStore.contextData.numberFormat.numberGroupSeparator;

            while (intString.length > 0) {
                if (intString.length > 3) {
                    const last3Digits = intString.slice(-3);
                    seperatedIntString = numberGroupSeparator + last3Digits + seperatedIntString;
                } else {
                    seperatedIntString = intString + seperatedIntString;
                }
                intString = intString.slice(0, -3);
            }

            const numberDecimalSeparator = this.mainStore.dataStore.contextData.numberFormat.numberDecimalSeparator;
            const fractionString = priceStringSplit[1];

            localizedPriceString = `${seperatedIntString}${numberDecimalSeparator}${fractionString}`;

            const currency = this.mainStore.dataStore.orderModule.flexSetPrice.currency.symbolShortForm;
            const prependCurrencySymbol =
                this.mainStore.dataStore.orderModule.flexSetPrice.currency.prependCurrencySymbol;

            if (prependCurrencySymbol)
                localizedPriceString = `${currency} ${localizedPriceString}`;
            else
                localizedPriceString = `${localizedPriceString} ${currency}`
            return localizedPriceString;
        } else {
            return '';
        }
    }

    updateBasketItemCount() {
        // if (typeof window === 'undefined') return; //only client side
        //
        // const basketApiUrl =
        //     this.mainStore.fetchedData.contextData.apiLink +
        //     'basket/Basket/BasketItemQuantities';
        //
        // const savKey = this.mainStore.fetchedData.orderModule.articleInfo.salesArticleVariantKey;
        // this.mainStore.fetch
        //     .getBasketItemCount(basketApiUrl, savKey)
        //     .then(response => {
        //         this.setArticleCount(response);
        //     })
        //     .catch(() => {
        //         this.setArticleCount(1);
        //     });
    }

    onPriceModeChangedEvent(newPriceMode: 'Gross' | 'Net') {
        // update withVat if it is out of sync with app shell event value
        if ((newPriceMode === 'Gross' && !this.withVat) || (newPriceMode === 'Net' && this.withVat)) {
            this.toggleWithVat();
        }
    }

    toggleWithVat = () => {
        this.withVat = !this.withVat;
    };

    setPriceModeCookie(newPriceMode: 'Net' | 'Gross'): void {
        const pricemodeMeta = document.querySelector('meta[name="current-pricemode"]');
        pricemodeMeta?.setAttribute('content', newPriceMode);
        
        const urlToHost = this.mainStore.dataStore.contextData.apiLink.replace('api/', '');
        const apiUrl = urlToHost + 'ajax/update/Content/Empty2';
        const requestURL = new URL(apiUrl);

        requestURL.searchParams.set('PriceMode', newPriceMode);
        axios
            .get(requestURL.toString())
            .then((res: AxiosResponse) => {
                if (res.status === 200 && typeof window !== 'undefined') {
                    window.shell.publishTo('PriceModeChanged', newPriceMode);
                }
            })
            .catch((error: Error | AxiosError) => {
                const errorMsg = `${error.name}: ${error.message}`;
                const errorStack = error.stack;

                // eslint-disable-next-line no-console
                console.warn(`fetch() failed with "${errorMsg}". Full error:\n${errorStack}`);
            });
    }

    saveVariantFlexSetId(): void {
        const urlToHost = this.mainStore.dataStore.contextData.apiLink.replace('api/', '');
        const apiUrl = urlToHost + 'api/flexiblesetpage/createvariantflexset';
        const requestURL = new URL(apiUrl);
        const bricksSelected = this.mainStore.dataStore.orderModule.sortedBrickSelections;
        const FSCode = this.mainStore.dataStore.orderModule.flexSetInfo.fsCode;
        const dataRefreshPayload = {
            'FSCode': FSCode,
            'Selections': bricksSelected
        };
        axios
            .post(requestURL.toString(), dataRefreshPayload, { timeout: 3500 })
            .then(response => {
                runInAction(() => {
                    this.variantFlexSetId = response.data;
                });
            })
            .catch(error => this.handleError(error));
    }

    private handleError(error: Error | AxiosError) {
        const errorMsg = this.isAxiosError(error) ? error.message : `${error.name}: ${error.message}`;
        const errorStack = this.isAxiosError(error) ? JSON.stringify(error.toJSON()) : error.stack;
        // eslint-disable-next-line no-console
        console.warn(`ajax fetch failed with "${errorMsg}". Full error:\n${errorStack}`);
        return 0;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private isAxiosError(error: any): error is AxiosError {
        return typeof error.toJSON !== 'undefined';
    }

    setArticleCount(param: number | ''): void {
        if (typeof param === 'number' && param > 0)
            this.articleCount = param;
        else
            this.articleCount = this.articleCountMin;
    }

    setStickHeaderHeight(param: number): void {
        this.stickHeaderHeight = param;
    }

    toggleCompareShareBookmarkMobileExpanded(): void {
        this.compareShareBookmarkMobileExpanded = !this.compareShareBookmarkMobileExpanded;
    }

    setSelectedBrickNo(newBrickNo: number | null): void {
        this.selectedBrickNo = newBrickNo;
    }

    // for piwik
    setSelectedBrickPosition(newPosition: number): void {
        this.selectedBrickPosition = newPosition;
    }

    get basePriceString() {
        let result = '';
        const basicPrice = this.mainStore.dataStore.orderModule.flexSetPrice.price;
        if (basicPrice.basePrice) {
            const priceString = this.getPriceString({
                grossValue: basicPrice.basePrice.grossValue,
                netValue: basicPrice.basePrice.netValue,
            });
            result = priceString;
        }
        return [result, basicPrice?.basePrice?.unit];
    }

    get titleAddon(): string {
        /*
        let result = '';

        const packagingInformation = this.mainStore.dataStore.orderModule.flexSetInfo.packagingInformation;
        if (packagingInformation.packageSize > 1) {
            let quantity = packagingInformation.quantityUnit.plural;
            let subQuantityUnit = '';
            if (packagingInformation.subQuantityUnit) {
                subQuantityUnit = `${packagingInformation.subQuantityUnit.plural} / `;
                quantity = packagingInformation.quantityUnit.singular;
            }
            result = `${packagingInformation.packageSize} ${subQuantityUnit}${quantity}`;
        }*/

        return '';
    }

    get deliveryTimeString(): string {
        const {
            periodType,
            minValue,
            maxValue,
            calendarWeek,
        } = this.mainStore.dataStore.orderModule.deliveryInformation;

        let deliveryTimeLocale;
        let result;
        if (!this.mainStore.isGlobal) {
            switch (periodType) {
                case 1:
                    deliveryTimeLocale = this.mainStore.localization.generalL10n.deliveryTimeUnknownDateLocale;
                    break;
                case 2:
                    deliveryTimeLocale = this.mainStore.localization.generalL10n.deliveryTimeFromCalWeekLocale;
                    break;
                default:
                    deliveryTimeLocale = this.mainStore.localization.generalL10n.deliveryTimeKnownDateLocale;
            }

            if (typeof window != 'undefined') {
                result =
                    periodType === 2
                        ? decodeHTML(deliveryTimeLocale) + ' ' + String(calendarWeek)
                        : decodeHTML(deliveryTimeLocale)?.replace('{0}', String(minValue))
                            .replace('{1}', String(maxValue));
            } else {
                result =
                    periodType === 2
                        ? deliveryTimeLocale + ' ' + String(calendarWeek)
                        : `~~~${deliveryTimeLocale}~${String(minValue)}~${String(maxValue)}~~~`;
            }
        } else {
            switch (this.mainStore.dataStore.orderModule.availabilityState) {
                case AvailabilityState.InStock:
                    deliveryTimeLocale = this.mainStore.localization.glbOnlyL10n?.deliveryDateShortGLBLocale;
                    break;
                case AvailabilityState.SoonInStock:
                    deliveryTimeLocale = this.mainStore.localization.glbOnlyL10n?.deliveryDateLongGLBLocale;
                    break;
                default:
                    deliveryTimeLocale = this.mainStore.localization.glbOnlyL10n?.deliveryDateLongGLBLocale;
            }
            result = typeof window != 'undefined' ? decodeHTML(deliveryTimeLocale) : deliveryTimeLocale;
        }
        return result;
    }

    get deliveryTimeStatusColor(): string {
        let statusColor;

        switch (this.mainStore.dataStore.orderModule.availabilityState) {
            case AvailabilityState.InStock:
                statusColor = defaultStyles.dpGreen;
                break;
            case AvailabilityState.SoonInStock:
                statusColor = defaultStyles.dpOrange;
                break;
            default:
                statusColor = defaultStyles.dpRed;
        }

        return statusColor;
    }

    get soldOutArticleStatusColor(): string {
        return defaultStyles.dpRed;
    }

    get isSale(): boolean {
        const articlePrice = this.mainStore.dataStore.orderModule.flexSetPrice;
        return articlePrice ? ((articlePrice.price.netValue / articlePrice.originalPrice.netValue) * 100) > 0 : false;
    }

    get shouldRenderBasePrice(): boolean {
        return !!this.mainStore.dataStore.orderModule.flexSetPrice.price.basePrice;
    }

    get articleUnit() {
        if (this.articleCount === 1) {
            return this.mainStore.localization.generalL10n.set;
        }
        else {
            return this.mainStore.localization.generalL10n.sets;
        }

        /*
        const packagingInformation = this.mainStore.dataStore.orderModule.flexSetInfo.packagingInformation;

        return this.articleCount > 1
            ? packagingInformation.quantityUnit.plural
            : packagingInformation.quantityUnit.singular;
            */
    }

    get basketModalData(): IBasketModal {
        const articleCount =
            this.mainStore.orderModuleStore.articleCount > 0
                ? this.mainStore.orderModuleStore.articleCount
                : this.articleCountMin;
        const basketModalItemOrigin = this.itemOrigin
            ? this.itemOrigin
            : 'NAVIGATION/' + this.mainStore.dataStore.category.name;
        const returnValue: IBasketModal = {
            brickSelections: this.mainStore.dataStore.orderModule.sortedBrickSelections,
            flexSetId: this.mainStore?.dataStore.orderModule.flexSetInfo.fsCode.toString(),
            basketItemId: this.mainStore.basketItemId,
            imageUrls: this.mainStore.dataStore.orderModule.sortedBrickSelections.map(item => item.imageFullPath),
            quantity: articleCount,
            // size: this.mainStore?.variantStore.selectedSize?.size.name,
            title: this.mainStore.dataStore.orderModule.flexSetInfo.displayName,
            maxQuantityPerOrder: this.mainStore.dataStore.orderModule.maxQuantityPerOrder,
            quantityUnit: '',
            /*this.mainStore.dataStore.orderModule.maxQuantityPerOrder > 1
                ? this.mainStore.dataStore.orderModule.flexSetInfo.packagingInformation.quantityUnit.plural
                : this.mainStore.dataStore.orderModule.flexSetInfo.packagingInformation.quantityUnit.singular,*/
            itemOrigin: basketModalItemOrigin,
            nativeNavKeyPath: this.mainStore.dataStore.category.navigationKeyPath,
            grossPrice: this.mainStore.dataStore.orderModule.flexSetPrice.price.grossValue.toString(),
            netPrice: this.mainStore.dataStore.orderModule.flexSetPrice.price.netValue.toString(),
            // isStraussBrand: !!this.mainStore.esBrand,
            isStraussBrand: true,
            categoryName: this.mainStore.dataStore.category.name,
            currencyISO: this.mainStore.dataStore.orderModule.flexSetPrice.currency.isoCode,
        };

        return returnValue as IBasketModal;
    }
}

interface IBasketModal {
    title: string;
    imageUrls: string[];
    flexSetId: string;
    basketItemId: string | null;
    brickSelections: IBrickSelection[];
    quantity: number;
    maxQuantityPerOrder?: number;
    quantityUnit?: string;
    itemOrigin?: string;
    nativeNavKeyPath?: string;
    salesArticleNo?: string;
    grossPrice?: string;
    netPrice?: string;
    scalePrice?: IPrice;
    isStraussBrand?: boolean;
    categoryName?: string;
    currencyISO?: string;
}

export default OrderModuleStore;
